import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

export const BILLING_QUANTITY_FLOW_OPTIONS = {
  inbound: [
    {
      value: "manual",
      label: "Enter quantity manually",
      description:
        "Choose this when your team should type the billable pallet, unit, or vault count directly on each inbound shipment.",
      example:
        'Example: A user enters "12 normal pallets" on the shipment before inbound charges are calculated.',
      Icon: EditOutlinedIcon,
      iconStyles: {
        color: "#8E24AA",
        backgroundColor: "rgba(142, 36, 170, 0.12)",
      },
    },
    {
      value: "automatic",
      label: "Calculate from shipment items",
      description:
        "Choose this when inbound billing quantity should be filled automatically from the shipment's received items, quantities, and pallet or unit details.",
      example:
        "Example: If a shipment receives 12 normal pallets, Glimpse fills the inbound billing quantity as 12 normal pallets.",
      Icon: LocalShippingOutlinedIcon,
      iconStyles: {
        color: "#1976D2",
        backgroundColor: "rgba(25, 118, 210, 0.12)",
      },
    },
  ],
  outbound: [
    {
      value: "manual",
      label: "Enter quantity manually",
      description:
        "Choose this when your team should type the billable pallet, unit, or vault count directly on each outbound order.",
      example:
        "Example: A user enters the shipped pallet or unit count on the order before outbound charges are calculated.",
      Icon: EditOutlinedIcon,
      iconStyles: {
        color: "#8E24AA",
        backgroundColor: "rgba(142, 36, 170, 0.12)",
      },
    },
    {
      value: "automatic",
      label: "Calculate from order items",
      description:
        "Choose this when outbound billing quantity should be filled automatically from the order's picked inventory items and asset details.",
      example:
        "Example: If an order ships 8 units, Glimpse fills the outbound billing quantity as 8 standard units.",
      Icon: Inventory2OutlinedIcon,
      iconStyles: {
        color: "#2E7D32",
        backgroundColor: "rgba(46, 125, 50, 0.12)",
      },
    },
  ],
  storage: [
    {
      value: "manual",
      label: "Calculate from storage locations",
      description:
        "Choose this when storage billing should be based on occupied locations or pallet count rules instead of warehouse quantity.",
      example:
        "Example: If an asset occupies 2 storage locations, Glimpse bills storage quantity from those locations.",
      Icon: LocationOnOutlinedIcon,
      iconStyles: {
        color: "#EF6C00",
        backgroundColor: "rgba(239, 108, 0, 0.12)",
      },
    },
    {
      value: "automatic",
      label: "Calculate from warehouse quantity",
      description:
        "Choose this when storage billing should use each asset's current warehouse quantity as the billable quantity.",
      example:
        "Example: If an asset has warehouse quantity 25, Glimpse uses 25 as the storage billing quantity.",
      Icon: WarehouseOutlinedIcon,
      iconStyles: {
        color: "#1976D2",
        backgroundColor: "rgba(25, 118, 210, 0.12)",
      },
    },
  ],
};

export const BILLING_QUANTITY_FLOW_LABELS = {
  inbound: "Inbound shipment charges",
  outbound: "Outbound order charges",
  storage: "Storage charges",
};

export const BILLING_QUANTITY_SOURCE_LABELS = Object.entries(BILLING_QUANTITY_FLOW_OPTIONS).reduce(
  (acc, [flow, options]) => {
    acc[flow] = options.reduce((flowAcc, option) => {
      flowAcc[option.value] = option.label;
      return flowAcc;
    }, {});
    return acc;
  },
  {}
);

export default function BillingQuantitySourceSelectionModal({
  open,
  flow,
  currentValue,
  onClose,
  onSelect,
}) {
  const [selected, setSelected] = useState(currentValue || "manual");

  useEffect(() => {
    if (open) {
      setSelected(currentValue || "manual");
    }
  }, [currentValue, open]);

  const options = useMemo(() => BILLING_QUANTITY_FLOW_OPTIONS[flow] || [], [flow]);
  const flowLabel = BILLING_QUANTITY_FLOW_LABELS[flow] || "Billing quantity source";

  const handleCancel = () => {
    setSelected(currentValue || "manual");
    onClose();
  };

  const handleSelect = (value) => {
    setSelected(value);
    onSelect(value);
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      onClose={handleCancel}
      PaperProps={{ sx: { width: "620px", maxWidth: "92vw" } }}
    >
      <DialogContent sx={{ bgcolor: "#f4f6f8" }}>
        <DialogContentText>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box width={40} />
            <Box textAlign="center">
              <Typography fontSize={22} fontWeight={400} color="text.primary">
                Select Quantity Source
              </Typography>
              <Typography fontSize={16} fontWeight={500} color="text.secondary" mt={0.5}>
                {flowLabel}
              </Typography>
            </Box>
            <IconButton color="textPrimary" onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box display="flex" flexDirection="column" gap={1.5}>
            {options.map((option) => {
              const isSelected = selected === option.value;
              const Icon = option.Icon;

              return (
                <Paper
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  elevation={isSelected ? 2 : 0}
                  sx={{
                    cursor: "pointer",
                    border: "2px solid",
                    borderColor: isSelected ? "primary.main" : "transparent",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                    boxShadow: isSelected ? "0 0 0 1px rgba(25, 118, 210, 0.08)" : "none",
                    "&:hover": {
                      borderColor: isSelected ? "primary.main" : "grey.300",
                    },
                  }}
                >
                  <Box p={2} display="flex" alignItems="center" gap={1.5}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: option.iconStyles.backgroundColor,
                        color: option.iconStyles.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography fontWeight={500} fontSize={15} color="text.primary">
                        {option.label}
                      </Typography>
                      <Typography
                        fontSize={14}
                        color="text.secondary"
                        sx={{ mt: 0.5, lineHeight: 1.5 }}
                      >
                        {option.description}
                      </Typography>
                      <Typography
                        fontSize={13}
                        color="text.disabled"
                        sx={{ mt: 0.75, lineHeight: 1.5 }}
                      >
                        {option.example}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
}
